import { Button, Col, Popconfirm, Row } from 'antd';
import React, { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
	cancelWorkOrderForBuyer,
	workOrdersRestCrudThunksForBuyer,
} from '../../../thunks/work_orders_thunks';
import {
	checkHasApprovalHierarchy,
	compareStrings,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../../utils/DataAccessUtils';
import { BUYER_ADMIN_ONLY, BUYER_ONLY_NO_STORE_ASSOCIATE } from '../../roles/roles';
import WorkOrderRowDisplay from '../../work_order_row_display/WorkOrderRowDisplay';
import { getLinkWIthBackLinkParams } from '../../../utils/HistoryUtils';
import AccessPermissionChecker from '../../common/AccessPermissionChecker';
import { isUserAllowedToAccess, PERMISSION_NAMES } from '../../../utils/AuthUtils';
import CancelWorkOrder from '../../cancel_work_order/CancelWorkOrder';
import { isInApprovalHierarchy } from '../../../utils/ApprovalHierarchyUtils';
import { WORK_ORDER_STATUSES } from '../../../constants/WorkOrderStatuses';
import { getWorkOrderLink } from '../../../utils/WorkOrderUtils';

require('./WorkOrderTableRow.less');

const WorkOrderTableRow = ({
	record,
	currentUser,
	companyConfig,
	cancelWorkOrder,
	handleArchive,
	refreshTable = (location?) => {},
	unarchiveWorkOrder,
	deleteWorkOrder,
	location,
	backText,
	targetCollectionName,
	actions,
}) => {
	const BACK_TEXT = backText || 'Back to work orders';
	const isApprovalAllowed = checkHasApprovalHierarchy(record)
		? isInApprovalHierarchy(currentUser, nullSafeGet('approvalHierarchy', record))
		: isUserAllowedToAccess('buyer')(
				PERMISSION_NAMES.CAN_APPROVE_WORK_ORDERS,
				companyConfig,
				currentUser.roles
		  );

	const workOrderActions = useMemo(() => {
		const workOrder = record;
		const actionsArray = [];
		if (workOrder === undefined) return actionsArray;
		if (actions) return actions;

		// Unarchive Action
		const unarchive =
			currentUser && currentUser.roles.some((role) => BUYER_ONLY_NO_STORE_ASSOCIATE.has(role)) ? (
				<div className={'ActionWrapper'}>
					<Button
						className={'ActionButton delete'}
						key="unarchive"
						onClick={(e) =>
							unarchiveWorkOrder(workOrder, e).then((e) => {
								refreshTable(location);
							})
						}
					>
						<i className="icons8-font icons8-trash-can"></i> Unarchive
					</Button>
				</div>
			) : null;

		// Delete Action
		const del =
			currentUser && currentUser.roles.some((role) => BUYER_ADMIN_ONLY.has(role)) ? (
				<div className={'ActionWrapper'}>
					<Popconfirm
						title={'Are you sure you want to permanently delete this work order?'}
						okText={'Permanently Delete'}
						onConfirm={(e) =>
							deleteWorkOrder(workOrder, e).then((e) => {
								refreshTable();
							})
						}
					>
						<Button className={'ActionButton delete'} key="delete">
							<i className="icons8-font icons8-trash-can"></i> Delete
						</Button>
					</Popconfirm>
				</div>
			) : null;

		if (compareStrings(WORK_ORDER_STATUSES.cancelledWithReason, workOrder.status)) {
			actionsArray.push(del);
		}
		// Add unarchive and delete actionsArray based on workOrder status
		if (workOrder.hasBuyerArchived) {
			actionsArray.push(unarchive);
		} else {
			// Cancel Action
			const cancel =
				currentUser && currentUser.roles.some((role) => BUYER_ONLY_NO_STORE_ASSOCIATE.has(role)) ? (
					<div className={'ActionWrapper'} onClick={(e) => e.stopPropagation()}>
						<CancelWorkOrder
							cancelButton={
								<Button className={'ActionButton cancel'} key="cancel">
									<i className="icons8-font icons8-font-margin-right icons8-cancel"></i> Cancel
								</Button>
							}
							workOrder={workOrder}
							refresh={() => refreshTable(location)}
						/>
					</div>
				) : nullSafeGetOrElse('config.newServiceRequest.allowCancel', companyConfig, false) ? (
					<div className={'ActionWrapper'} onClick={(e) => e.stopPropagation()}>
						<CancelWorkOrder
							cancelButton={
								<Button className={'ActionButton cancel'} key="cancel">
									<i className="icons8-font icons8-font-margin-right icons8-cancel"></i> Cancel
								</Button>
							}
							workOrder={workOrder}
							refresh={() => refreshTable(location)}
						/>
					</div>
				) : null;

			// Archive Action
			const archive = (
				<div className={'ActionWrapper'}>
					<Button
						className={'ActionButton delete'}
						key="archive"
						onClick={(e) =>
							handleArchive(workOrder, e).then((e) => {
								refreshTable(location);
							})
						}
					>
						<i className="icons8-font icons8-font-margin-right icons8-trash-can"></i> Archive
					</Button>
				</div>
			);

			// Edit Action
			const edit =
				currentUser && currentUser.roles.some((role) => BUYER_ONLY_NO_STORE_ASSOCIATE.has(role)) ? (
					<AccessPermissionChecker name={PERMISSION_NAMES.EDIT_WORK_ORDERS}>
						<div className={'ActionWrapper'}>
							<Link
								className={'ActionLink'}
								to={getLinkWIthBackLinkParams(
									location,
									BACK_TEXT,
									`/buyer/workOrders/edit/${workOrder.id}`
								)}
							>
								<Button className={'ActionButton edit'} key="edit">
									<i className="icons8-font icons8-font-margin-right icons8-edit"></i> Edit
								</Button>
							</Link>
						</div>
					</AccessPermissionChecker>
				) : null;

			// Approve Action
			const approve = isApprovalAllowed ? (
				<div className={'ActionWrapper'}>
					<Link
						className={'ActionLink'}
						to={getLinkWIthBackLinkParams(
							location,
							BACK_TEXT,
							`/buyer/workOrders/approve/${workOrder.id}`
						)}
					>
						<Button className={'ActionButton approve'} key="new">
							<i className="icons8-font icons8-font-margin-right icons8-green-check-mark"></i>{' '}
							Approve
						</Button>
					</Link>
				</div>
			) : null;

			// Add actionsArray based on display status
			switch (workOrder.displayStatus) {
				case 'Pending':
					actionsArray.push(approve, cancel);
					break;
				case 'Open':
					actionsArray.push(edit, cancel);
					break;
				case 'InProgress':
					actionsArray.push(edit, cancel);
					break;
				case 'OnHold':
					actionsArray.push(cancel);
					break;
				case 'Completed':
					actionsArray.push(archive);
					break;
				case 'Cancelled':
					actionsArray.push(archive);
					break;
				default:
					break;
			}
		}

		return actionsArray;
	}, [
		record,
		actions,
		BACK_TEXT,
		currentUser,
		companyConfig,
		refreshTable,
		unarchiveWorkOrder,
		deleteWorkOrder,
		location,
		isApprovalAllowed,
		handleArchive,
	]);

	const getLink = useCallback(() => {
		return getWorkOrderLink({
			userType: 'buyer',
			tab: 'details',
			workOrder: record,
			targetCollectionName,
			location,
			BACK_TEXT,
		});
	}, [BACK_TEXT, location, record, targetCollectionName]);

	return (
		<Row className="index-row-wrapper group" style={{ display: 'flex', flexWrap: 'nowrap' }}>
			<Col
				style={{ display: 'flex', flexGrow: 1, minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}
			>
				<Link
					className="index-padding"
					style={{ width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column' }}
					to={getLink()}
					draggable={false}
				>
					<WorkOrderRowDisplay workOrder={record} refreshTable={refreshTable} />
				</Link>
			</Col>
			<Col
				className={'ActionRow'}
				style={{
					gridTemplateColumns: `repeat(${
						nullSafeGetOrElse('length', workOrderActions, 2) > 1 ? 2 : 1
					}, auto[col-end])`,
					flexShrink: 0,
				}}
			>
				{workOrderActions}
			</Col>
		</Row>
	);
};

const mapDispatchToProps = (dispatch) => ({
	cancelWorkOrder: (entity) => dispatch(cancelWorkOrderForBuyer(entity)),
	handleArchive: (entity) => dispatch(workOrdersRestCrudThunksForBuyer.archive(entity, 'id', true)),
	unarchiveWorkOrder: (entity) =>
		dispatch(workOrdersRestCrudThunksForBuyer.unarchive(entity, 'id', true)),
	deleteWorkOrder: (entity) =>
		dispatch(workOrdersRestCrudThunksForBuyer.delete(entity, 'id', null, true)),
});

export default connect(null, mapDispatchToProps)(WorkOrderTableRow);
